import { scopeProjectRef } from "@amoreg/client-runtime/environment";
import { type HairlineProps, Settle } from "@lucasmarkes/hairline/react";
import { createFileRoute } from "@tanstack/react-router";
import { RotateCcwIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";

import { Button } from "../components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "../components/ui/empty";
import { HairlineArt } from "../components/ui/hairline-art";
import { SidebarInset } from "../components/ui/sidebar";
import { useNewThreadHandler } from "../hooks/useHandleNewThread";
import { useAllEnvironmentShellsBootstrapped, useProjects } from "../state/entities";
import { recordVisitedOrg, useActiveOrgId } from "../state/orgs";

/**
 * `/$org/chat`, the rail's Chat: drops straight into a draft thread for the
 * tenant, so Chat opens on a prompt instead of a dead end. (The org's own
 * landing, `/$org`, is Home.) The org's
 * workspace is the project whose id equals the org id (the mirror invariant);
 * the URL segment is the org slug (id-tolerant), so the id comes from the
 * resolver, not the path. Until the mirror has created the workspace, a
 * quiet setting-up state shows instead.
 */
function OrgChatRouteView() {
  const orgId = useActiveOrgId();
  const projects = useProjects();
  const bootstrapped = useAllEnvironmentShellsBootstrapped();
  const handleNewThread = useNewThreadHandler();
  const startingRef = useRef(false);
  const [startState, setStartState] = useState({ failed: false, retryRequest: 0 });

  const orgProject = useMemo(
    () => (bootstrapped ? (projects.find((project) => project.id === orgId) ?? null) : null),
    [bootstrapped, projects, orgId],
  );

  useEffect(() => {
    if (orgId !== null) {
      recordVisitedOrg(orgId);
    }
  }, [orgId]);

  // A new org id means a new landing: allow another draft start.
  useEffect(() => {
    startingRef.current = false;
  }, [orgId]);

  useEffect(() => {
    if (orgProject === null || startingRef.current) {
      return;
    }
    startingRef.current = true;
    void handleNewThread(scopeProjectRef(orgProject.environmentId, orgProject.id), {
      replace: true,
    }).catch(() => {
      startingRef.current = false;
      setStartState((state) => ({ ...state, failed: true }));
    });
  }, [handleNewThread, orgProject, startState.retryRequest]);

  if (!bootstrapped) {
    return null;
  }
  if (orgProject !== null) {
    return startState.failed ? (
      <OrgIndexHero
        title="Couldn’t start a new thread"
        description="The workspace is still available. Try opening the draft again."
        action={
          <Button
            size="sm"
            onClick={() =>
              setStartState((state) => ({ failed: false, retryRequest: state.retryRequest + 1 }))
            }
          >
            <RotateCcwIcon className="size-4" />
            Try again
          </Button>
        }
      />
    ) : null;
  }
  return (
    <OrgIndexHero
      art={Settle}
      title="Setting up this workspace"
      description="The organization’s workspace is being prepared. It appears automatically — usually within half a minute."
    />
  );
}

function OrgIndexHero({
  title,
  description,
  action,
  art,
}: {
  readonly title: string;
  readonly description: string;
  readonly action?: React.ReactNode;
  readonly art?: ComponentType<HairlineProps>;
}) {
  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <Empty className="flex-1">
        <EmptyHeader className="max-w-md">
          {art ? <HairlineArt figure={art} play className="mb-6 w-64 sm:w-72" /> : null}
          <EmptyTitle className="text-foreground text-xl">{title}</EmptyTitle>
          <EmptyDescription className="mt-2 text-sm text-muted-foreground/78">
            {description}
          </EmptyDescription>
          {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
        </EmptyHeader>
      </Empty>
    </SidebarInset>
  );
}

export const Route = createFileRoute("/_chat/$org/chat")({
  component: OrgChatRouteView,
});
